import React, { useEffect, useMemo, useState } from 'react';
import { Card, Space, Button, Table, Form, Input, Select, Modal, Popconfirm, message, Row, Col, DatePicker, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import apiService from '../../services/api';

const { Option } = Select;

export default function VeiculosManagement({ branches = [] }) {
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ placa: '', branchId: undefined });
  const [usageFilters, setUsageFilters] = useState({ usuarioCodigo: '', dia: null, placa: '' });
  const [usageResults, setUsageResults] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);

  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [didChange, setDidChange] = useState(false);

  const loadVehicles = async () => {
    setLoading(true);
    try {
      const resp = await apiService.veiculo.list({ placa: filters.placa || undefined, branchId: filters.branchId || undefined, limit: 50 });
      // O backend retorna shape { data, total, page, limit, totalPages }
      // Mantemos compatibilidade com possíveis shapes antigos (items ou array direto)
      const data = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.items)
          ? resp.items
          : Array.isArray(resp)
            ? resp
            : [];
      setVehicles(data);
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Erro ao carregar veículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVehicles(); }, []);

  const columns = [
    { title: 'Placa', dataIndex: 'placa', key: 'placa' },
    { 
      title: 'Filial', 
      dataIndex: ['branch', 'name'], 
      key: 'branchName', 
      render: (_, r) => r.branch?.name || r.branchName || '-',
      responsive: ['sm', 'md', 'lg', 'xl']
    },
    { title: 'm³', dataIndex: 'm3', key: 'm3', responsive: ['md', 'lg', 'xl'] },
    { title: 'Peso', dataIndex: 'peso', key: 'peso', responsive: ['md', 'lg', 'xl'] },
    { 
      title: 'Ações', 
      key: 'actions', 
      render: (_, record) => (
        <Space wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(record)}>Editar</Button>
          <Popconfirm title={`Excluir veículo ${record.placa}?`} onConfirm={() => onDelete(record)}>
            <Button size="small" danger icon={<DeleteOutlined />}>Excluir</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const onEdit = (record) => {
    setEditing(record);
    editForm.setFieldsValue({ placa: record.placa, branchId: record.branchId, m3: record.m3, peso: record.peso });
    setEditVisible(true);
  };

  const onDelete = async (record) => {
    try {
      await apiService.veiculo.delete(record.id);
      message.success('Veículo excluído');
      loadVehicles();
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Erro ao excluir veículo');
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      const data = { placa: values.placa.trim().toUpperCase(), branchId: values.branchId, m3: Number(values.m3 || 0), peso: Number(values.peso || 0) };
      await apiService.veiculo.create(data);
      message.success('Veículo criado');
      setCreateVisible(false);
      createForm.resetFields();
      setDidChange(true);
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Erro ao criar veículo');
    }
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      const data = { placa: values.placa.trim().toUpperCase(), branchId: values.branchId, m3: Number(values.m3 || 0), peso: Number(values.peso || 0) };
      await apiService.veiculo.update(editing.id, data);
      message.success('Veículo atualizado');
      setEditVisible(false);
      editForm.resetFields();
      setDidChange(true);
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Erro ao atualizar veículo');
    }
  };

  const searchUsages = async () => {
    setUsageLoading(true);
    try {
      const hojeStr = usageFilters.dia ? usageFilters.dia.format('YYYY-MM-DD') : undefined;
      const resp = await apiService.veiculoUso.list({ usuarioCodigo: usageFilters.usuarioCodigo || undefined, dia: hojeStr, placa: usageFilters.placa || undefined });
      // Normaliza o shape do retorno de usos
      const usosArr = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.items)
          ? resp.items
          : Array.isArray(resp)
            ? resp
            : [];
      // O backend já inclui veiculo e user; evitamos requisições extras
      const results = usosArr.map((uso) => {
        const placa = uso?.placa ?? uso?.veiculo?.placa ?? uso?.vehiclePlaca ?? null;
        const diaVal = uso?.dtUso ?? uso?.dia ?? uso?.data ?? uso?.date ?? uso?.createdAt ?? null;
        const usuarioCodigo = uso?.usuarioCodigo ?? uso?.user?.codigo ?? uso?.userCodigo ?? null;
        return { ...uso, placa, dia: diaVal, usuarioCodigo };
      });
      setUsageResults(results);
      message.info(`Encontrados ${results.length} usos`);
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Erro ao buscar usos');
    } finally {
      setUsageLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={[8, 8]}>
          <Col xs={20} sm={10} md={8} lg={4}>
            <Input placeholder="Placa" value={filters.placa} onChange={e => setFilters(prev => ({ ...prev, placa: e.target.value }))} allowClear />
          </Col>
          <Col xs={20} sm={10} md={6} lg={4}>
            <Select placeholder="Filial" value={filters.branchId} onChange={val => setFilters(prev => ({ ...prev, branchId: val }))} allowClear style={{ width: '100%' }}>
              {branches?.map(b => (<Option key={b.id} value={b.id}>{b.name} ({b.sigla})</Option>))}
            </Select>
          </Col>
          <Col xs={20} sm={10} md={6} lg={4}>
            <Button block icon={<SearchOutlined />} onClick={loadVehicles}>Filtrar</Button>
          </Col>
          <Col xs={20} sm={10} md={6} lg={4}>
            <Button block icon={<ReloadOutlined />} onClick={() => { setFilters({ placa: '', branchId: undefined }); loadVehicles(); }}>Limpar</Button>
          </Col>
          <Col xs={20} sm={10} md={6} lg={4}>
            <Button block type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>Novo Veículo</Button>
          </Col>
        </Row>
      </div>

      <Card title="Veículos">
        <Table 
          rowKey="id" 
          dataSource={vehicles} 
          columns={columns} 
          loading={loading} 
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 10 }} 
        />
      </Card>

      <Card title="Usos por usuário" style={{ marginTop: 16 }}>
        <Row gutter={[8, 8]}>
          <Col xs={24} md={6}>
            <Input 
              placeholder="Placa"
              value={usageFilters.placa}
              allowClear
              onChange={e => setUsageFilters(prev => ({ ...prev, placa: e.target.value }))}
              onBlur={(e) => {
                const v = (e.target.value || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                setUsageFilters(prev => ({ ...prev, placa: v }));
              }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Input placeholder="Código do usuário" value={usageFilters.usuarioCodigo} onChange={e => setUsageFilters(prev => ({ ...prev, usuarioCodigo: e.target.value }))} allowClear />
          </Col>
          <Col xs={24} md={6}>
            <DatePicker placeholder="Dia" value={usageFilters.dia} onChange={val => setUsageFilters(prev => ({ ...prev, dia: val }))} format="YYYY-MM-DD" style={{ width: '100%' }} />
          </Col>
          <Col xs={24} md={6}>
            <Button block icon={<SearchOutlined />} onClick={searchUsages}>Buscar Usos</Button>
          </Col>
        </Row>
        <Table
          style={{ marginTop: 12 }}
          size="small"
          rowKey="id"
          dataSource={usageResults}
          loading={usageLoading}
          scroll={{ x: 'max-content' }}
          columns={[
            { 
              title: 'Código do usuário', 
              key: 'usuarioCodigo', 
              render: (_, r) => r?.usuarioCodigo ?? r?.user?.codigo ?? '-' 
            },
            { 
              title: 'Dia', 
              key: 'dia', 
              render: (_, r) => {
                const v = r?.dia || r?.dtUso || r?.data || r?.date || r?.createdAt;
                return v ? String(v).slice(0, 10) : '-';
              }
            },
            { 
              title: 'Placa', 
              key: 'placa', 
              render: (_, r) => r?.placa ?? r?.veiculo?.placa ?? '-' 
            },
          ]}
          pagination={{ pageSize: 10 }}
        />
      </Card>

        <Modal open={createVisible} title="Novo Veículo" onCancel={() => setCreateVisible(false)} onOk={handleCreate} okText="Salvar" cancelText="Cancelar" destroyOnHidden maskClosable={false} keyboard={false} afterOpenChange={(open) => { if (!open && didChange) { loadVehicles(); setDidChange(false); } }}>
        <Form form={createForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="placa" label="Placa" rules={[{ required: true, message: 'Informe a placa' }, { min: 7, max: 8 }, { pattern: /^[A-Za-z0-9]+$/, message: 'A placa deve conter apenas letras e números' }]}>
                <Input 
                  maxLength={8}
                  allowClear
                  onChange={(e) => {
                    const raw = e.target.value || '';
                    const hasInvalid = /[^A-Za-z0-9]/.test(raw);
                    const sanitized = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                    if (hasInvalid) {
                      message.warning('A placa deve conter apenas letras e números');
                    }
                    createForm.setFieldsValue({ placa: sanitized });
                  }}
                  onBlur={() => {
                    const v = createForm.getFieldValue('placa') || '';
                    createForm.setFieldsValue({ placa: v.trim().toUpperCase() });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="branchId" label="Filial" rules={[{ required: true, message: 'Selecione a filial' }]}> 
                <Select placeholder="Selecione">
                  {branches?.map(b => (<Option key={b.id} value={b.id}>{b.name} </Option>))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item 
                name="m3" 
                label="m³" 
                rules={[{
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') return Promise.resolve();
                    const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '.'));
                    return Number.isFinite(num) ? Promise.resolve() : Promise.reject(new Error('m³ deve ser número'));
                  }
                }]}
              > 
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  precision={2}
                  style={{ width: '100%' }}
                  parser={(v) => (v || '').replace(/,/g, '.').replace(/[^0-9.]/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="peso" 
                label="Peso (Toneladas)" 
                rules={[{
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') return Promise.resolve();
                    const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '.'));
                    return Number.isFinite(num) ? Promise.resolve() : Promise.reject(new Error('Peso deve ser número'));
                  }
                }]}
              > 
                <InputNumber 
                  min={0} 
                  step={0.001} 
                  precision={3}
                  style={{ width: '100%' }}
                  parser={(v) => (v || '').replace(/,/g, '.').replace(/[^0-9.]/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

        <Modal open={editVisible} title={`Editar Veículo ${editing?.placa || ''}`} onCancel={() => setEditVisible(false)} onOk={handleUpdate} okText="Salvar" cancelText="Cancelar" destroyOnHidden maskClosable={false} keyboard={false} afterOpenChange={(open) => { if (!open && didChange) { loadVehicles(); setDidChange(false); } }}>
        <Form form={editForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="placa" label="Placa" rules={[{ required: true, message: 'Informe a placa' }, { min: 7, max: 8 }, { pattern: /^[A-Za-z0-9]+$/, message: 'A placa deve conter apenas letras e números' }]}> 
                <Input 
                  placeholder="ABC1D23"
                  maxLength={8}
                  allowClear
                  onChange={(e) => {
                    const raw = e.target.value || '';
                    const hasInvalid = /[^A-Za-z0-9]/.test(raw);
                    const sanitized = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                    if (hasInvalid) {
                      message.warning('A placa deve conter apenas letras e números');
                    }
                    editForm.setFieldsValue({ placa: sanitized });
                  }}
                  onBlur={() => {
                    const v = editForm.getFieldValue('placa') || '';
                    editForm.setFieldsValue({ placa: v.trim().toUpperCase() });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="branchId" label="Filial" rules={[{ required: true, message: 'Selecione a filial' }]}> 
                <Select placeholder="Selecione">
                  {branches?.map(b => (<Option key={b.id} value={b.id}>{b.name} ({b.sigla})</Option>))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item 
                name="m3" 
                label="m³" 
                rules={[{
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') return Promise.resolve();
                    const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '.'));
                    return Number.isFinite(num) ? Promise.resolve() : Promise.reject(new Error('m³ deve ser número'));
                  }
                }]}
              > 
                <InputNumber 
                  min={0} 
                  step={0.01} 
                  precision={2}
                  style={{ width: '100%' }}
                  parser={(v) => (v || '').replace(/,/g, '.').replace(/[^0-9.]/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="peso" 
                label="Peso (Toneladas)" 
                rules={[{
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') return Promise.resolve();
                    const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '.'));
                    return Number.isFinite(num) ? Promise.resolve() : Promise.reject(new Error('Peso deve ser número'));
                  }
                }]}
              > 
                <InputNumber 
                  min={0} 
                  step={0.001} 
                  precision={3}
                  style={{ width: '100%' }}
                  parser={(v) => (v || '').replace(/,/g, '.').replace(/[^0-9.]/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}